import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useZuordnungKandidaten } from '../../api/bedarfe';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { BewerberStatusBadge } from '../bewerber/BewerberStatusBadge';
import { BewerberStatus } from '../../types/bewerber';
import type { ZuordnungKandidat } from '../../types/bedarf';

interface SchulbegleiterSelectProps {
  bedarfId: string;
  /** Whether someone already holds the position — only changes the wording. */
  hasCurrent: boolean;
  disabled?: boolean;
  onSelect: (kandidat: ZuordnungKandidat) => void;
}

/**
 * The search field in the header of the Schulbegleiter card. Modelled on
 * BedarfSelect, but the search runs on the server: there are far more Bewerber
 * than Bedarfe, and only GEEIGNET..GEWONNEN may be assigned at all.
 *
 * It only picks — confirming a replacement and saving are the card's business.
 */
export function SchulbegleiterSelect({
  bedarfId,
  hasCurrent,
  disabled = false,
  onSelect,
}: SchulbegleiterSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebouncedValue(searchText.trim(), 250);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: kandidaten = [], isLoading, isFetching } = useZuordnungKandidaten(
    bedarfId,
    debouncedSearch,
    isOpen,
  );

  const close = () => {
    setIsOpen(false);
    setSearchText('');
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        close();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (kandidat: ZuordnungKandidat) => {
    if (kandidat.zugeordnetHier || kandidat.zugeordnetAnderswo) return;
    onSelect(kandidat);
    close();
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <svg
          className="pointer-events-none absolute inset-y-0 left-2 my-auto h-4 w-4 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
        </svg>
        <input
          type="text"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => !disabled && setIsOpen(true)}
          onKeyDown={(e) => e.key === 'Escape' && close()}
          placeholder={hasCurrent ? 'Ändern…' : 'Zuordnen…'}
          disabled={disabled}
          aria-label="Schulbegleiter suchen"
          autoComplete="off"
          className="block w-40 rounded-md border-gray-300 py-1 pl-7 pr-2 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-100"
        />
      </div>

      {/* Right-aligned and wider than the input: the card sits in the narrow
          sidebar, and name, status and city would not fit in 10rem. */}
      {isOpen && (
        <div className="absolute right-0 z-50 mt-1 w-80 max-h-80 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {isLoading ? (
            <div className="px-4 py-3 text-sm text-gray-500">Laden…</div>
          ) : kandidaten.length > 0 ? (
            <ul className={`py-1 ${isFetching ? 'opacity-60' : ''}`}>
              {kandidaten.map((k) => {
                const blocked = k.zugeordnetHier || k.zugeordnetAnderswo !== null;
                return (
                  <li key={k.id}>
                    <div
                      role="option"
                      aria-selected={k.zugeordnetHier}
                      aria-disabled={blocked}
                      onClick={() => handleSelect(k)}
                      className={`px-4 py-2 ${
                        blocked ? 'cursor-default bg-gray-50' : 'cursor-pointer hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className={`text-sm font-medium ${blocked ? 'text-gray-500' : 'text-gray-900'}`}
                        >
                          {k.firstName} {k.lastName}
                        </span>
                        <BewerberStatusBadge status={k.status as BewerberStatus} />
                      </div>
                      {k.zugeordnetHier ? (
                        <div className="text-xs text-emerald-700">Ist dieser Stelle zugeordnet</div>
                      ) : k.zugeordnetAnderswo ? (
                        <div className="text-xs text-gray-500">
                          Bereits zugeordnet:{' '}
                          <Link
                            to={`/bedarfe/${k.zugeordnetAnderswo.id}`}
                            className="text-primary-600 hover:text-primary-500"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {k.zugeordnetAnderswo.name}
                          </Link>
                        </div>
                      ) : (
                        (k.cityName || k.email) && (
                          <div className="truncate text-xs text-gray-500">
                            {[k.cityName, k.email].filter(Boolean).join(' · ')}
                          </div>
                        )
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="px-4 py-3 text-sm text-gray-500">
              {debouncedSearch ? 'Kein passender Bewerber' : 'Keine Bewerber verfügbar'}
            </div>
          )}
          {/* Twenty rows is a first page, not everyone — say so rather than
              let a missing name look like a missing person. */}
          {kandidaten.length === 20 && (
            <div className="border-t border-gray-100 px-4 py-2 text-xs text-gray-400">
              Nur die ersten 20 — Suche eingrenzen
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SchulbegleiterSelect;
